import { useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { Info, ChevronDown, ChevronRight, Database, Calculator, AlertTriangle, Flag } from "lucide-react";
import { resolvePageDoc } from "@shared/page-docs";
import { useDocTab } from "@/components/layout/DocTabContext";
import { FeedbackButton } from "./FeedbackButton";

export function AboutThisPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [expanded, setExpanded] = useState(false);
  const hubTab = useDocTab();
  // #729: merged Pages sync their active Tab to ?tab= — resolve the most
  // specific doc (path+tab, else path) instead of keying by pathname alone.
  // #1721 #3564: the tab a hub is showing (useHubTabs sets it on every hub)
  // wins over the URL, which can name a tab the role cannot open.
  const urlTab = searchParams.get("tab");
  const tab = hubTab ?? urlTab;
  const docs = resolvePageDoc(location.pathname, tab);
  // Feedback names the Tab the reader was on. When the hub shows the tab the
  // URL names (or this is not a hub), that is the URL's tab, doc or no doc.
  // When the hub fell back, it is the fallback tab if that has a doc of its
  // own, else the bare path.
  const feedbackTab =
    hubTab === null || hubTab === urlTab
      ? urlTab
      : docs !== resolvePageDoc(location.pathname, null)
        ? tab
        : null;
  const feedbackPage = feedbackTab ? `${location.pathname}?tab=${feedbackTab}` : location.pathname;

  if (!docs) return null;

  return (
    <div className="mt-8 rounded-xl border border-border bg-card print:hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted/30 transition-colors text-left"
      >
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="h-4 w-4" />
          <span className="font-medium whitespace-nowrap">About this page</span>
          <span className="text-xs">· {docs.title} (v{docs.version.replace("v", "")})</span>
        </div>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-border px-5 py-4 space-y-5 text-sm">
          {/* Description */}
          <p className="text-muted-foreground leading-relaxed">
            {docs.description}
          </p>

          {/* Data Sources */}
          <div>
            <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              <Database className="h-3.5 w-3.5" />
              Data Sources
            </h4>
            <div className="space-y-1.5">
              {docs.dataSources.map((ds) => (
                <div
                  key={ds.name}
                  className="flex items-start gap-2 text-sm"
                >
                  <span className="text-muted-foreground/40 mt-1">•</span>
                  <div>
                    <span className="font-medium text-foreground">
                      {ds.name}
                    </span>
                    {ds.table && (
                      <span className="font-mono text-xs text-muted-foreground ml-1">
                        ({ds.table})
                      </span>
                    )}
                    <span className="text-muted-foreground"> · {ds.source}</span>
                    {ds.refresh && (
                      <span className="text-muted-foreground/60 text-xs ml-1">
                        [{ds.refresh}]
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Calculations */}
          <div>
            <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              <Calculator className="h-3.5 w-3.5" />
              Calculations
            </h4>
            <div className="space-y-2">
              {docs.calculations.map((calc) => (
                <div
                  key={calc.metric}
                  className="rounded-lg bg-muted/30 px-3 py-2"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium text-foreground text-sm">
                      {calc.metric}
                    </span>
                  </div>
                  <p className="font-mono text-xs text-primary mt-0.5">
                    {calc.formula}
                  </p>
                  {calc.notes && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {calc.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Limitations */}
          {docs.limitations.length > 0 && (
            <div>
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                <AlertTriangle className="h-3.5 w-3.5" />
                Known Limitations
              </h4>
              <ul className="space-y-1">
                {docs.limitations.map((lim, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-muted-foreground"
                  >
                    <span className="text-amber-600 dark:text-amber-400/60 mt-0.5 shrink-0">⚠</span>
                    {lim}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-border/50 pt-3">
            <p className="text-xs text-muted-foreground/50">
              Last updated: {docs.lastUpdated} · {docs.version}
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Flag className="h-3 w-3" />
              <span>See an error?</span>
              <FeedbackButton
                page={feedbackPage}
                section="Page Documentation"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
